<template>
  <div class="z-pb-1200">
    <YContainer>
      <z-breadcrumb
        :items="[
          { text: 'Home', url: 'https://querobolsa.com.br/' },
          { text: 'Manual do Enem', url: '/enem/manual-do-enem' },
          { text: category.name, url: `/enem/${$route.params.category}` },
          { text: lesson.title },
        ]"
      />

      <CampaignBanner
        :campaign-data="campaignData"
        :is-loading="campaignLoading"
      />

      <z-title
        class="lesson__title"
        tag="h1"
      >
        {{ lesson.title }}
      </z-title>

      <z-text
        class="lesson__info"
        weight="bold"
        size="small"
        color="minor"
      >
        {{ category.name }} - Manual do Enem
      </z-text>
      <div class="lesson__info">
        <div
          v-if="authorFullName"
          class="lesson__author"
        >
          <z-avatar
            v-if="authorAvatar"
            class="lesson__author-avatar"
            :aria-label="authorFullName"
            size="small"
            :src="authorAvatar"
          />

          <z-avatar
            v-else-if="!authorAvatar"
            class="lesson__author-avatar"
            aria-label="Imagem do autor"
            size="small"
          />

          <z-text
            class="lesson__author-name"
            weight="bold"
            size="small"
            color="minor"
          >
            Publicado por {{ authorFullName }}
          </z-text>
        </div>

        <z-text
          v-if="authorFullName && showDate"
          weight="bold"
          size="small"
          color="minor"
        >
          &nbsp;-&nbsp;
        </z-text>

        <z-text
          v-if="showDate"
          class="lesson__creation"
          weight="bold"
          size="small"
          color="minor"
        >
          Última atualização: {{ showDate }}
        </z-text>
      </div>
    </YContainer>

    <YContainer>
      <section
        v-for="item in filteredIntroduction"
        :key="item.id"
        class="lesson__section lesson__section--aside"
      >
        <z-title
          :id="item.id"
          tag="h2"
          size="small"
          class="lesson__section-title"
        >
          {{ item.title }}
        </z-title>

        <div
          class="lesson__section-content"
          v-html="item.content"
        />
      </section>

      <section class="lesson__section lesson__section--aside">
        <z-title
          tag="h2"
          size="small"
          class="lesson__section-title"
        >
          Índice
        </z-title>

        <div class="lesson__section-content">
          <div class="lesson__summary">
            <z-link
              v-for="(item, index) in summary"
              :key="item.id"
              tag="button"
              @click="onSummaryClick(item.id)"
            >
              {{ index + 1 }}) {{ item.text }}
            </z-link>
          </div>
        </div>
      </section>

      <section
        v-for="item in removeIntroduction"
        :key="item.id"
        class="lesson__section lesson__section--aside"
      >
        <z-title
          :id="item.id"
          tag="h2"
          size="small"
          class="lesson__section-title"
        >
          {{ item.title }}
        </z-title>

        <div
          class="lesson__section-content"
          v-html="item.content"
        />
      </section>

      <section
        v-if="lesson.videos && lesson.videos.length"
        class="lesson__section lesson__section--aside"
      >
        <div class="lesson__section-content">
          <LessonVideoGallery :videos="lesson.videos" />
        </div>
      </section>

      <section
        v-if="lesson.exercises && lesson.exercises.length"
        id="exercises"
        class="lesson__section lesson__section--aside"
      >
        <div class="lesson__section-content">
          <LessonExercises :exercises="lesson.exercises" />
        </div>
      </section>

      <section
        class="lesson__section lesson__section--aside"
      >
        <div class="lesson__section-content">
          <MiniBio
            v-if="showMiniBio"
            class="mini__bio-content"
            :author-name="authorFullName"
            :author-image="authorAvatar"
            :author-biography="authorBiography"
          />
        </div>
      </section>

      <section
        v-if="relatedLessons.length"
        class="lesson__section lesson__section--aside"
      >
        <div class="lesson__section-content">
          <z-title
            tag="h3"
            size="extra-small"
          >
            Conteúdos relacionados
          </z-title>

          <div class="lesson__related-lessons">
            <div
              v-for="relatedLesson in relatedLessons"
              :key="relatedLesson.slug"
              class="lesson__related-lesson"
            >
              <VLazyImage
                :src="imageSource(relatedLesson.cover_image)"
                :alt="relatedLesson.title"
                class="lesson__related-lesson-cover"
              />

              <z-link
                :href="`/enem/${category.slug}/${relatedLesson.slug}`"
              >
                {{ relatedLesson.title }}
              </z-link>
            </div>
          </div>
        </div>
      </section>
      <div>
        <LeadCollectorSection
          visitable-type="aaa"
        />
      </div>
    </YContainer>
  </div>
</template>

<script>
import VLazyImage from 'v-lazy-image/v2'
import { YContainer } from '@quero/yeti'

import LessonVideoGallery from '~/components/lesson/lesson-video-gallery'
import LessonExercises from '~/components/lesson/lesson-exercises'
import LeadCollectorSection from '~/components/lead-collector/lead-collector-section'

import { parseLessonContent } from '~/assets/js/services/lessons'
import { getLessonsBySlug, getLessonsByTags } from '~/assets/js/services/graphqlApi'
import { retry } from '~/assets/js/utils/http'
import { CAMPAIGN_CONFIG_URL } from '~/utils/constants/campaignConfig'

import CampaignBanner from '~/components/campaign-banner'
import MiniBio from '~/components/lesson/mini-bio'

export default {
  name: 'LessonIndex',
  components: {
    CampaignBanner,
    LessonExercises,
    LessonVideoGallery,
    YContainer,
    VLazyImage,
    LeadCollectorSection,
    MiniBio,
  },
  async asyncData ({ $cms, error, params }) {
    try {
      let query = getLessonsBySlug(params.lesson)
      let graphqlQuery = {
        query,
      }
      const lesson = await retry(
        () => $cms.$post('/graphql', graphqlQuery),
        { retries: 10 },
      ).then((response) => {
        return response.data.ManualDoEnemAulas[0]
      })

      const { author, category } = lesson

      let tags = ''

      lesson.tags.forEach((tag) => {
        tags = tags + `"${tag.ManualDoEnemTags_id.name}",`
      })

      tags = tags.replace(/,\s*$/, '')

      const hasExercises = Boolean(lesson.exercises?.length)
      const { summary, contents } = parseLessonContent(lesson.text, hasExercises)

      let relatedLessons = []

      if (tags) {
        query = getLessonsByTags(tags)

        graphqlQuery = {
          query,
        }

        relatedLessons = await retry(
          () => $cms.$post('/graphql', graphqlQuery),
          { retries: 10 },
        ).then((response) => {
          return response.data.ManualDoEnemAulas
        })
      }

      const campaignRequest = await retry(
        () => $cms.$get(CAMPAIGN_CONFIG_URL),
        { retries: 2 },
      ).catch((err) => {
        error({ statusCode: 500, message: 'Failed to fetch campaign.\n' + err.message })
      })

      return {
        lesson,
        category,
        author,
        summary,
        contents,
        campaignData: campaignRequest?.data,
        relatedLessons: relatedLessons
          .slice(0, 4),
      }
    } catch (err) {
      error({
        statusCode: 500,
        message: `Failed to fetch lesson data for ${params.lesson}, error: ${err}`,
      })
    }
  },
  data () {
    return {
      lesson: {},
      category: {},
      author: {},
      summary: [],
      contents: [],
      relatedLessons: [],
      campaignData: [],
      campaignLoading: true,
    }
  },
  head () {
    return {
      title: `${this.lesson.title} - Manual do Enem`,
      meta: [
        { hid: 'description', name: 'description', content: this.lesson.meta_description || 'Aqui no Manual do Enem, você encontra conteúdos, videos e exercícios relacionados ao Enem e Vestibulares. Aprenda Matemática, Redação, Biologia, Português, etc.' },
      ],
      script: [{
        src: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.5/MathJax.js?config=TeX-MML-AM_CHTML',
        async: true,
        defer: true,
      }],
    }
  },
  computed: {
    authorFullName () {
      if (!this.author) return null
      return `${this.author.name} ${this.author.surname}`.trim()
    },
    authorAvatar () {
      return (this.author?.avatar)
        ? this.$imageboss(this.author?.avatar)
        : null
    },
    authorBiography () {
      return this.author?.biography
    },
    showMiniBio () {
      return this.authorFullName && this.authorBiography
    },
    showDate () {
      const latestDate = this.lesson.date_created < this.lesson.date_updated
        ? this.lesson.date_updated
        : this.lesson.date_created

      const dateObj = new Date(latestDate)

      if (!isNaN(dateObj)) {
        return dateObj.getDate() + '/' + (dateObj.getMonth() + 1) + '/' + dateObj.getFullYear()
      }

      return ''
    },
    filteredIntroduction () {
      return this.contents.filter(item => item.title === 'Introdução')
    },
    removeIntroduction () {
      return this.contents.filter(item => item.title !== 'Introdução')
    },
  },
  mounted () {
    MathJax.Hub.Config({
      CommonHTML: { linebreaks: { automatic: true } },
    })

    MathJax.Hub.Typeset()
    this.campaignLoading = false
  },
  methods: {
    onSummaryClick (id) {
      document.getElementById(id).scrollIntoView({
        behavior: 'smooth',
      })
    },

    imageSource (coverImage) {
      return this.$imageboss(coverImage)
    },
  },
}
</script>

<style lang="scss" scoped>
.lesson__title {
  margin-top: var(--unit-0800);
  margin-bottom: 0;
}

.lesson__info {
    align-items: center;
    display: flex;
    margin-top: var(--unit-0200);
}

.lesson__author {
  align-items: center;
  display: flex;
}

.lesson__section {
  padding-bottom: var(--unit-2000);

  &:first-child {
    padding-top: var(--unit-2000);
  }
}

.lesson__section--aside {
  @media (min-width: $breakpoint-tablet) {
    column-gap: var(--unit-0600);
    display: grid;
    grid-template-areas: (
      'title content'
    );
    grid-template-columns: 1fr 2fr;
  }
}

.lesson__section-title {
  grid-area: title;
  margin: 0;
}

.lesson__section-content {
  grid-area: content;
}

.lesson__author-avatar {
  margin-right: var(--unit-0200);
}

.lesson__section-content::v-deep {
  & p:first-child {
    margin-top: 0;
  }

  & p:last-child {
    margin-bottom: 0;
  }

  & .fr-img-caption {
    display: block;
    margin-bottom: var(--unit-0800);
    margin-top: var(--unit-0800);
    text-align: center;
    width: 100%;
  }

  & .fr-img-wrap {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;

    & img {
      width: 100%;
    }
  }

  & .fr-inner {
    font-weight: var(--font-weight-medium);
    text-transform: capitalize;
  }

  & table {
    display: block;
    max-width: fit-content;
    overflow-x: auto;
  }

  & img {
    max-width: 100%;
  }
}

.lesson__summary {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: var(--unit-0400);
}

.lesson__related-lessons {
  display: grid;
  gap: var(--unit-0600);

  @media (min-width: $breakpoint-tablet) {
    grid-template-columns: repeat(2, 1fr)
  }
}

.lesson__related-lesson {
  display: flex;
  gap: var(--unit-0400);
}

.lesson__related-lesson-cover {
  border-radius: var(--border-radius-small);
  box-shadow: var(--elevation-down-10);
  height: var(--unit-1600);
  object-fit: cover;
  width: var(--unit-1600);
}
</style>
